import { h, Fragment } from 'preact';
import useFormStore from '../../store/useFormStore';
import { formatCurrency } from '../cart/cartHelpers';

export default function AppointmentDetailsBlock({ appointmentNumber, services, timeslot }) {
  // Safety check: if services or timeslot is missing, return null
  if (!services || !timeslot) {
    console.warn('AppointmentDetailsBlock: Missing required props', { services, timeslot });
    return null;
  }

  // Extract serviceIds and serviceNames from services object
  const serviceIds = Object.keys(services);
  const serviceNames = Object.values(services);
  const selectedServicesConfiguration = useFormStore((state) => state.selectedServicesConfiguration);
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const cart = useFormStore((state) => state.cart);
  const getServiceById = useFormStore((state) => state.getServiceById);

  // Check if this franchise uses pricing
  const usesPricing = currentFranchise?.uses_pricing !== false; // Default to true if undefined

  // Format the date to "Thursday, February 5" format
  const formatDate = (dateObj) => {
    if (!dateObj) return '';

    // Convert to Date object if it's a string
    const dateInstance = dateObj instanceof Date ? dateObj : new Date(dateObj);

    const options = { weekday: 'long', month: 'long', day: 'numeric' };
    return dateInstance.toLocaleDateString('en-US', options);
  };

  // Get the service configuration summary for a single service (total quantity, addons, and pricing)
  const getServiceSummary = (serviceId, serviceName) => {
    if (!serviceId || !selectedServicesConfiguration[serviceId]) {
      return { totalQuantity: 0, addons: [], subtotal: 0 };
    }

    const serviceConfig = selectedServicesConfiguration[serviceId];
    const serviceDefinition = getServiceById(serviceId);

    if (!serviceDefinition) {
      return { totalQuantity: 0, addons: [], subtotal: 0 };
    }

    let totalQuantity = 0;
    let subtotal = 0;
    const addonsMap = {};
    const formColumns = serviceDefinition.form?.columns || [];
    const firstColumnKey = formColumns[0]?.label?.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

    // Get pricing from cart categorySummary if available
    const categorySummary = cart?.categorySummary?.[serviceName];

    Object.entries(serviceConfig).forEach(([rowId, columns]) => {
      Object.entries(columns).forEach(([columnKey, quantity]) => {
        if (quantity > 0) {
          const isMainService = columnKey.toLowerCase() === firstColumnKey;

          if (isMainService) {
            totalQuantity += quantity;
          } else {
            // Find the column definition for the label
            const columnDef = formColumns.find(
              col => col.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') === columnKey.toLowerCase()
            );
            const columnLabel = columnDef?.label || columnKey;

            // Aggregate addon quantities
            if (!addonsMap[columnLabel]) {
              addonsMap[columnLabel] = { quantity: 0, subtotal: 0 };
            }
            addonsMap[columnLabel].quantity += quantity;
          }
        }
      });
    });

    // Get pricing from cart if available
    if (categorySummary) {
      subtotal = categorySummary.subtotal || 0;

      // Get addon pricing
      Object.entries(categorySummary.addons || {}).forEach(([addonLabel, addonData]) => {
        if (addonsMap[addonLabel]) {
          addonsMap[addonLabel].subtotal = addonData.subtotal || 0;
        }
      });
    }

    // Convert addons map to array
    const addons = Object.entries(addonsMap).map(([label, data]) => ({
      label,
      quantity: data.quantity,
      subtotal: data.subtotal
    }));

    return { totalQuantity, addons, subtotal, serviceDefinition };
  };

  return (
    <div className="appointment-details bg-white p-6 shadow-sm rounded-lg border border-gray-200 flex-1">
      <div className="text-2xl font-bold mb-6">Appointment #{appointmentNumber} Details</div>

      <div className="mb-4">
        <div className="text-sm text-gray-600 mb-1">Appointment Date</div>
        <div className="text-xl font-bold">{formatDate(timeslot.dateObj)}</div>
      </div>

      <hr className="my-4 border-gray-300" />

      <div className="mb-4">
        <div className="text-sm text-gray-600 mb-1">Arrival Window</div>
        <div className="text-xl font-bold">{timeslot.timeslot}</div>
      </div>

      <hr className="my-4 border-gray-300" />

      <div>
        <div className="text-sm text-gray-600 mb-2">{serviceIds.length > 1 ? 'Services' : 'Service'}</div>

        {/* Display each service */}
        <div className="flex flex-col gap-4">
          {serviceIds.map((serviceId, index) => {
            const serviceName = serviceNames[index];
            const serviceSummary = getServiceSummary(serviceId, serviceName);

            return (
              <Fragment key={serviceId}>
                {serviceSummary.totalQuantity > 0 && (
                  <div className="flex flex-col gap-1">
                    {/* Main Service Row */}
                    <div className={`grid ${usesPricing ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                      <div className="font-bold col-span-2">
                        {serviceName}
                      </div>
                      <div className="text-right">
                        {Drupal.formatPlural(
                          serviceSummary.totalQuantity,
                          serviceSummary.serviceDefinition?.labels?.singular ?? "@count item",
                          serviceSummary.serviceDefinition?.labels?.plural ?? "@count items"
                        )}
                      </div>
                      {usesPricing && (
                        <div className="text-right">
                          {formatCurrency(serviceSummary.subtotal)}
                        </div>
                      )}
                    </div>

                    {/* Addon Rows */}
                    {serviceSummary.addons.length > 0 && (
                      <div className="text-base">
                        {serviceSummary.addons.map((addon, addonIndex) => {
                          const addonQuantityText = Drupal.formatPlural(
                            addon.quantity,
                            serviceSummary.serviceDefinition?.labels?.singular ?? "@count item",
                            serviceSummary.serviceDefinition?.labels?.plural ?? "@count items"
                          );

                          return (
                            <div key={addonIndex} className={`grid ${usesPricing ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                              <div className="col-span-2 pl-6">
                                {addon.label}
                              </div>
                              <div className="text-right">
                                {addonQuantityText}
                              </div>
                              {usesPricing && (
                                <div className="text-right">
                                  {formatCurrency(addon.subtotal)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
